import React from "react";
import { useParams, Link, useNavigate } from "react-router";
import {
  ArrowLeft,
  GitBranch,
  ChevronDown,
  ChevronUp,
  Trash2,
} from "lucide-react";
import { useScript, useDeleteScript } from "../../hooks/useScripts";
import {
  useDuplicateScript,
  useApproveScript,
  useRejectScript,
} from "../../hooks/useScripts";
import { useNodes } from "../../hooks/useNodes";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import FlowToolbar from "../../components/flows/FlowToolbar";
import { usePanZoom } from "../../hooks/usePanZoom";
import NodeGraph, { NodePosition } from "../../components/flows/NodeGraph";
import NodeEditorSidebar from "../../components/flows/NodeEditorSidebar";
// import TestingConfigPanel from '../../components/testing/TestingConfigPanel';
import { toast } from "react-hot-toast";
import api from "../../services/api";
import ScriptForm from "../../components/scripts/ScriptForm";
import { useUpdateScript } from "../../hooks/useScripts";
import { useCampaigns, useCampaign } from "../../hooks/useCampaigns";
import NodeCard from "../../components/nodes/NodeCard";
import NodeFiltersPanel from "../../components/nodes/NodeFiltersPanel";
// ADD: scriptApi to check for existing drafts before duplicating
import { scriptApi } from "../../services/api";
import FlowAuditSection, {
  FlowAuditSectionRef,
} from "../../components/flows/FlowAuditSection";
import { useAuth } from "../../hooks/useAuth";
import FlowTrackEventsSection from "../../components/flows/FlowTrackEventsSection";

interface Transition {
  id: number | string;
  current_node_id: number;
  next_node_id: number;
}

export default function FlowDetailV2Page() {
  const { scriptId } = useParams<{ scriptId: string }>();
  const scriptIdNum = scriptId ? parseInt(scriptId, 10) : 0;
  const navigate = useNavigate();
  const deleteScriptMutation = useDeleteScript();
  const duplicateScriptMutation = useDuplicateScript();
  const approveScriptMutation = useApproveScript();
  const rejectScriptMutation = useRejectScript();
  const {
    data: script,
    isLoading: scriptLoading,
    error: scriptError,
  } = useScript(scriptIdNum);
  const [search, setSearch] = React.useState("");
  const [inputType, setInputType] = React.useState("");
  const [globalOnly, setGlobalOnly] = React.useState(false);
  const [messageSearch, setMessageSearch] = React.useState("");
  const {
    nodes,
    loading: nodesLoading,
    refreshNodes,
    createNode,
    updateNode,
    deleteNode,
    cloneNode,
    total,
  } = useNodes({
    scriptId: scriptIdNum,
    fetchAll: true,
    pageSize: 200,
    search: search || undefined,
    inputType: inputType || undefined,
    globalOnly,
  });
  // Also load all global nodes to offer as transition targets in flows
  const { nodes: globalNodes } = useNodes({
    fetchAll: true,
    pageSize: 500,
    globalOnly: true,
  });
  const { zoom, pan, onCanvasMouseDown, setZoom, resetView } = usePanZoom(1);
  const [positions, setPositions] = React.useState<NodePosition[]>([]);
  const [transitions, setTransitions] = React.useState<Transition[]>([]);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [editingNode, setEditingNode] = React.useState<any | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasUnsavedPositions, setHasUnsavedPositions] = React.useState(false);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [isDuplicating, setIsDuplicating] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { mutateAsync: updateScriptMutate, isPending: isUpdatingScript } =
    useUpdateScript();
  const { data: campaignsData } = useCampaigns({ page_size: 200 });
  const { data: currentCampaign } = useCampaign(script?.campaign_id || 0);
  const [viewMode, setViewMode] = React.useState<"tree" | "list">("tree");
  const [focusedPair, setFocusedPair] = React.useState<{
    fromId: number;
    toId: number;
  } | null>(null);
  const { user } = useAuth();
  const isAdmin = (user?.permissions || []).includes("admin");
  const canEdit = isAdmin || (user?.permissions || []).includes("flows.edit");
  const canReview =
    isAdmin || (user?.permissions || []).includes("flows.review");
  const auditSectionRef = React.useRef<FlowAuditSectionRef>(null);
  const idToNameMap = React.useMemo(() => {
    const map: Record<number, string> = {};
    (nodes || []).forEach((n: any) => {
      map[n.id] = n.node_name;
    });
    return map;
  }, [nodes]);
  // Merge script nodes with all global nodes for selection in the sidebar
  const nodesForSelection = React.useMemo(() => {
    const byId = new Map<
      number,
      { id: number; node_name: string; script_id?: number | null }
    >();
    for (const n of nodes || [])
      byId.set(n.id, {
        id: n.id,
        node_name: n.node_name,
        script_id: (n as any).script_id,
      });
    for (const g of globalNodes || [])
      byId.set(g.id, {
        id: g.id,
        node_name: g.node_name,
        script_id: (g as any).script_id,
      });
    return Array.from(byId.values()).sort((a, b) =>
      a.node_name.localeCompare(b.node_name),
    );
  }, [nodes, globalNodes]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/scripts/${scriptIdNum}/node-positions`);
        setPositions(
          (res.data?.positions || []).map((p: NodePosition) => ({
            id: p.id,
            x: Math.max(0, p.x),
            y: Math.max(0, p.y),
          })),
        );
      } catch {}
    })();
  }, [scriptIdNum]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/scripts/${scriptIdNum}/transitions`);
        setTransitions(res.data?.transitions || []);
      } catch {}
    })();
  }, [scriptIdNum]);

  React.useEffect(() => {
    if (!nodes || nodes.length === 0) return;
    setPositions((prev) => {
      const have = new Set(prev.map((p) => p.id));
      const next = [...prev];
      let idx = 0;
      for (const n of nodes) {
        if (!have.has(n.id)) {
          const col = idx % 4;
          const row = Math.floor(idx / 4);
          next.push({ id: n.id, x: 80 + col * 260, y: 80 + row * 160 });
          idx += 1;
        }
      }
      return next;
    });
  }, [nodes]);

  const savePositions = async () => {
    setIsSaving(true);
    try {
      await api.put(`/scripts/${scriptIdNum}/node-positions`, { positions });
      setHasUnsavedPositions(false);
      toast.success("Node positions saved");
    } catch {
      toast.error("Failed to save node positions");
    } finally {
      setIsSaving(false);
    }
  };

  const openSidebarForNode = (node: any) => {
    setEditingNode(node);
    setIsCreating(false);
    setSidebarOpen(true);
  };
  const openCreateSidebar = () => {
    setEditingNode(null);
    setIsCreating(true);
    setSidebarOpen(true);
  };

  const handleUpdateNode = async (data: any) => {
    if (!editingNode) return;
    setIsSubmitting(true);
    try {
      await updateNode(editingNode.id, data);
      await refreshNodes();
      setSidebarOpen(false);
      // Refresh audit log after node update
      auditSectionRef.current?.refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNode = async (data: any) => {
    setIsSubmitting(true);
    try {
      await createNode({ ...data, script_id: scriptIdNum });
      await refreshNodes();
      setSidebarOpen(false);
      setIsCreating(false);
      // Refresh audit log after node creation
      auditSectionRef.current?.refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNode = async () => {
    if (!editingNode) return;
    const confirmed = window.confirm(
      "Delete this node? This action cannot be undone.",
    );
    if (!confirmed) return;
    setIsSubmitting(true);
    try {
      await deleteNode(editingNode.id);
      await refreshNodes();
      setPositions((prev) => prev.filter((p) => p.id !== editingNode.id));
      setSidebarOpen(false);
      toast.success("Node deleted");
      // Refresh audit log after node deletion
      auditSectionRef.current?.refresh();
    } catch {
      toast.error("Failed to delete node");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteNodeFromList = async (node: any) => {
    const confirmed = window.confirm(
      `Delete node "${node.node_name}"? This action cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      await deleteNode(node.id);
      await refreshNodes();
      setPositions((prev) => prev.filter((p) => p.id !== node.id));
      toast.success("Node deleted");
      // Refresh audit log after node deletion
      auditSectionRef.current?.refresh();
    } catch {
      toast.error("Failed to delete node");
    }
  };

  const handleCloneNodeFromList = async (node: any) => {
    try {
      const defaultName = `${node.node_name}_copy`;
      const newName =
        window.prompt("New node name", defaultName) || defaultName;
      await cloneNode(node.id, newName, scriptIdNum);
      await refreshNodes();
      toast.success("Node cloned");
      // Refresh audit log after node cloning
      auditSectionRef.current?.refresh();
    } catch {
      toast.error("Failed to clone node");
    }
  };

  const handleUpdateScriptInfo = async (data: any) => {
    try {
      await updateScriptMutate({ id: scriptIdNum, data });
      setInfoOpen(false);
      // Refresh audit log after script update
      auditSectionRef.current?.refresh();
    } catch {}
  };

  const handleDeleteFlow = async () => {
    const confirmed = window.confirm(
      "Delete this flow? This action cannot be undone.",
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await deleteScriptMutation.mutateAsync(scriptIdNum);
      toast.success("Flow deleted");
      navigate("/flows");
    } catch (e) {
      // error toast handled in hook
    } finally {
      setIsDeleting(false);
    }
  };

  const onDuplicateAsDraft = async () => {
    if (!script) return;
    setIsDuplicating(true);
    try {
      // Determine lineage root id
      const rootId = script.parent_script_id || script.id;
      // Fetch scripts for this campaign and find an existing draft in same lineage
      const resp = await scriptApi.getScripts({
        campaign_id: script.campaign_id,
        page: 1,
        page_size: 200,
      });
      const existingDrafts = (resp.scripts || []).filter(
        (s: any) => s.status === "draft" && s.parent_script_id === rootId,
      );
      if (existingDrafts.length > 0) {
        // Pick the most recently updated draft
        existingDrafts.sort(
          (a: any, b: any) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        const draft = existingDrafts[0];
        toast.success("Redirected to existing draft");
        navigate(`/flows/${draft.id}`);
        return;
      }
      // No existing draft, proceed to create a new one
      const draft = await duplicateScriptMutation.mutateAsync({
        id: script.id,
        data: { change_summary: "Proposed edits", created_by: "admin" },
      });
      navigate(`/flows/${draft.id}`);
    } catch {
    } finally {
      setIsDuplicating(false);
    }
  };

  const onApprove = async () => {
    if (!script) return;
    try {
      await approveScriptMutation.mutateAsync({
        id: script.id,
        data: { approved_by: "admin" },
      });
      // Refresh audit log after script approval
      auditSectionRef.current?.refresh();
    } catch {}
  };

  const onReject = async () => {
    if (!script) return;
    try {
      await rejectScriptMutation.mutateAsync({
        id: script.id,
        data: { reason: "Not approved" },
      });
      // Refresh audit log after script rejection
      auditSectionRef.current?.refresh();
    } catch {}
  };

  const handleClearFilters = () => {
    setSearch("");
    setInputType("");
    setGlobalOnly(false);
    setMessageSearch("");
    setFocusedPair(null);
    setViewMode("list");
  };

  const handleTransitionClick = (fromId: number, toId: number) => {
    // Clear other filters to ensure both nodes are visible
    setSearch("");
    setInputType("");
    setGlobalOnly(false);
    setMessageSearch("");
    setFocusedPair({ fromId, toId });
    setViewMode("list");
  };

  const filteredNodes = React.useMemo(() => {
    if (!messageSearch) return nodes;
    try {
      const query = messageSearch.toLowerCase();
      return (nodes || []).filter((n: any) => {
        const msgs: any[] = Array.isArray(n.messages) ? n.messages : [];
        return msgs.some(
          (m) =>
            typeof m?.content === "string" &&
            m.content.toLowerCase().includes(query),
        );
      });
    } catch {
      return nodes;
    }
  }, [nodes, messageSearch]);

  const displayNodes = React.useMemo(() => {
    if (!focusedPair) return filteredNodes;
    const allowed = new Set([focusedPair.fromId, focusedPair.toId]);
    return (nodes || []).filter((n: any) => allowed.has(n.id));
  }, [filteredNodes, focusedPair, nodes]);

  // Simple auto-layout: level by BFS from roots; fall back to grid if cycles or no transitions
  const autoLayout = React.useCallback(() => {
    if (!nodes || nodes.length === 0) return;
    const idSet = new Set((nodes || []).map((n: any) => n.id));
    const outgoing = new Map<number, number[]>();
    const incomingCount = new Map<number, number>();
    for (const n of nodes as any[]) {
      outgoing.set(n.id, []);
      incomingCount.set(n.id, 0);
    }
    for (const t of transitions || []) {
      if (!idSet.has(t.current_node_id) || !idSet.has(t.next_node_id)) continue;
      outgoing.get(t.current_node_id)!.push(t.next_node_id);
      incomingCount.set(
        t.next_node_id,
        (incomingCount.get(t.next_node_id) || 0) + 1,
      );
    }
    const roots: number[] = [];
    incomingCount.forEach((cnt, id) => {
      if (cnt === 0) roots.push(id);
    });
    const queue: Array<{ id: number; level: number }> = roots.length
      ? roots.map((id) => ({ id, level: 0 }))
      : (nodes as any[]).map((n) => ({ id: n.id, level: 0 }));
    const seen = new Set<number>();
    const levels = new Map<number, number>();
    while (queue.length) {
      const { id, level } = queue.shift()!;
      if (seen.has(id)) continue;
      seen.add(id);
      levels.set(id, Math.max(levels.get(id) || 0, level));
      for (const nb of outgoing.get(id) || []) {
        if (!seen.has(nb)) queue.push({ id: nb, level: level + 1 });
      }
    }
    // Group nodes by level
    const levelToIds = new Map<number, number[]>();
    for (const n of nodes as any[]) {
      const lvl = levels.get(n.id) ?? 0;
      if (!levelToIds.has(lvl)) levelToIds.set(lvl, []);
      levelToIds.get(lvl)!.push(n.id);
    }
    const sortedLevels = Array.from(levelToIds.keys()).sort((a, b) => a - b);
    const xSpacing = 260;
    const ySpacing = 160;
    const leftPad = 80;
    const topPad = 80;
    const newPositions: NodePosition[] = [];
    let maxWidth = 0;
    for (let i = 0; i < sortedLevels.length; i++) {
      const ids = levelToIds.get(sortedLevels[i])!;
      // sort ids for consistency by name if available
      ids.sort((a, b) => {
        const an = idToNameMap[a] || "";
        const bn = idToNameMap[b] || "";
        return an.localeCompare(bn);
      });
      for (let j = 0; j < ids.length; j++) {
        const x = leftPad + j * xSpacing;
        const y = topPad + i * ySpacing;
        newPositions.push({ id: ids[j], x, y });
        if (j > maxWidth) maxWidth = j;
      }
    }
    if (newPositions.length === 0) {
      // fallback to grid
      const cols = 4;
      const gridPositions: NodePosition[] = (nodes as any[]).map(
        (n: any, idx: number) => {
          const col = idx % cols;
          const row = Math.floor(idx / cols);
          return {
            id: n.id,
            x: leftPad + col * xSpacing,
            y: topPad + row * ySpacing,
          };
        },
      );
      setPositions(gridPositions);
      setHasUnsavedPositions(true);
      return;
    }
    setPositions(newPositions);
    setHasUnsavedPositions(true);
  }, [nodes, transitions, idToNameMap]);

  if (scriptLoading)
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  if (scriptError || !script)
    return (
      <ErrorMessage
        message="Failed to load flow"
        onRetry={() => {}}
        className="py-12"
      />
    );

  const campaignOptions = (campaignsData?.campaigns || []).map((c: any) => ({
    id: c.id,
    name: c.name,
  }));
  const hasCurrentCampaign =
    script?.campaign_id &&
    campaignOptions.some((o) => o.id === script.campaign_id);
  const mergedCampaignOptions =
    hasCurrentCampaign || !script?.campaign_id
      ? campaignOptions
      : [
          {
            id: script.campaign_id,
            name: currentCampaign?.name || "Current campaign",
          },
          ...campaignOptions,
        ];

  const isSpecialEditingNode =
    !!editingNode &&
    typeof editingNode?.node_name === "string" &&
    ["start", "end_conversation"].includes(editingNode.node_name.toLowerCase());

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/flows" className="text-gray-600 hover:text-gray-800">
          <ArrowLeft className="inline h-5 w-5" /> Back
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GitBranch className="h-6 w-6 text-blue-500" /> {script.name}
          {script.status === "published" && (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Published
            </span>
          )}
          {script.status === "draft" && (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Draft
            </span>
          )}
          {script.status === "archived" && (
            <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
              Archived
            </span>
          )}
        </h1>
      </div>

      {/* Flow Information Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Flow information
            </h2>
            <p className="text-sm text-gray-500">
              Update the basic details for this flow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Draft workflow actions */}
            {script.status === "published" && canEdit && (
              <button
                onClick={onDuplicateAsDraft}
                className="inline-flex items-center px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                disabled={!canEdit || isDuplicating}
              >
                {isDuplicating ? (
                  <>
                    <span className="inline-block animate-spin rounded-full border-2 border-white border-t-transparent h-4 w-4 mr-2" />
                    Duplicating...
                  </>
                ) : (
                  "Duplicate as Draft"
                )}
              </button>
            )}
            {script.status === "draft" && (
              <>
                {canReview && (
                  <button
                    onClick={onApprove}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                    disabled={!canReview}
                  >
                    Approve & Publish
                  </button>
                )}
                {canReview && (
                  <button
                    onClick={onReject}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-yellow-600 text-white text-sm font-medium hover:bg-yellow-700"
                    disabled={!canReview}
                  >
                    Reject
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => setInfoOpen((o) => !o)}
              className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50"
            >
              {infoOpen ? (
                <ChevronUp className="h-4 w-4 mr-1" />
              ) : (
                <ChevronDown className="h-4 w-4 mr-1" />
              )}
              {infoOpen ? "Hide" : "Show"}
            </button>
            {canEdit && (
              <button
                onClick={handleDeleteFlow}
                className="inline-flex items-center px-3 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="inline-block animate-spin rounded-full border-2 border-white border-t-transparent h-4 w-4 mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete Flow
                  </>
                )}
              </button>
            )}
          </div>
        </div>
        {infoOpen ? (
          <div className="p-6">
            <ScriptForm
              script={script}
              onSubmit={handleUpdateScriptInfo}
              onCancel={() => setInfoOpen(false)}
              isLoading={isUpdatingScript}
              submitText="Save Flow"
              hideReplayNodeGroups
              campaignOptions={mergedCampaignOptions}
            />
          </div>
        ) : (
          <div className="px-6 py-4 text-sm text-gray-700">
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-gray-500">Name:</span>{" "}
                <span className="font-medium">{script.name}</span>
              </div>
              <div>
                <span className="text-gray-500">Campaign ID:</span>{" "}
                <span className="font-medium">{script.campaign_id}</span>
              </div>
              <div>
                <span className="text-gray-500">Version:</span>{" "}
                <span className="font-medium">{script.version_number}</span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>{" "}
                <span className="font-medium">
                  {script.status ||
                    (script.is_published ? "published" : "draft")}
                </span>
              </div>
            </div>
            {script.description ? (
              <p className="mt-2 text-gray-600">{script.description}</p>
            ) : null}
          </div>
        )}
      </div>

      {/* Track Events Section */}
      {script?.id ? (
        <div className="mt-4">
          <FlowTrackEventsSection
            scriptId={script.id}
            readOnly={script?.status !== "draft" || !canEdit}
          />
        </div>
      ) : null}

      {/* Flow Audit Section */}
      {script?.id ? (
        <FlowAuditSection ref={auditSectionRef} scriptId={script.id} />
      ) : null}

      <hr className="my-2 border-gray-200" />

      {/* View Toggle + Toolbar */}
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-md shadow-sm border border-gray-200">
          <button
            className={`px-4 py-2 text-sm font-medium ${viewMode === "tree" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"} rounded-l-md`}
            onClick={() => setViewMode("tree")}
          >
            Tree
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium ${viewMode === "list" ? "bg-blue-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"} rounded-r-md border-l border-gray-200`}
            onClick={() => setViewMode("list")}
          >
            List
          </button>
        </div>
        {viewMode === "tree" && (
          <FlowToolbar
            onZoomIn={() => setZoom((z) => Math.min(2, z + 0.1))}
            onZoomOut={() => setZoom((z) => Math.max(0.3, z - 0.1))}
            onReset={resetView}
            onSave={savePositions}
            isSaving={isSaving}
            canSave={hasUnsavedPositions}
            onAddNode={
              script?.status === "draft" ? openCreateSidebar : undefined
            }
            onAutoLayout={autoLayout}
          />
        )}
      </div>

      {viewMode === "tree" ? (
        <NodeGraph
          nodes={nodes}
          positions={positions}
          onPositionsChange={(next) => setPositions(next)}
          onPositionsDirty={(dirty) => setHasUnsavedPositions(dirty)}
          zoom={zoom}
          panX={pan.x}
          panY={pan.y}
          onCanvasMouseDown={onCanvasMouseDown}
          transitions={transitions}
          onEditNode={(node) => {
            setEditingNode(node);
            setIsCreating(false);
            setSidebarOpen(true);
          }}
          loading={nodesLoading}
          highlightGlobal
        />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {displayNodes.length} of {total} nodes
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshNodes}
                disabled={nodesLoading}
                className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Refresh
              </button>
              {script?.status === "draft" && (
                <button
                  onClick={openCreateSidebar}
                  className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Create Node
                </button>
              )}
            </div>
          </div>

          {focusedPair && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-100 text-blue-800 px-3 py-2 rounded">
              <div className="text-sm">
                Focused on transition:{" "}
                <span className="font-medium">
                  {idToNameMap[focusedPair.fromId] ||
                    `Node ${focusedPair.fromId}`}
                </span>{" "}
                →{" "}
                <span className="font-medium">
                  {idToNameMap[focusedPair.toId] || `Node ${focusedPair.toId}`}
                </span>
              </div>
              <button
                onClick={() => setFocusedPair(null)}
                className="text-sm text-blue-700 hover:underline"
              >
                Clear
              </button>
            </div>
          )}

          <NodeFiltersPanel
            search={search}
            onSearchChange={setSearch}
            scriptId={scriptId?.toString() || ""}
            onScriptIdChange={() => {}}
            inputType={inputType}
            onInputTypeChange={setInputType}
            globalOnly={globalOnly}
            onGlobalOnlyChange={setGlobalOnly}
            scripts={[]}
            onClearFilters={handleClearFilters}
            hideScriptFilter
            hideGlobalOnly
            messageSearch={messageSearch}
            onMessageSearchChange={setMessageSearch}
            hideMessageSearch={false}
          />

          {nodesLoading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayNodes.map((node: any) => (
                <NodeCard
                  key={node.id}
                  node={node}
                  onEdit={openSidebarForNode}
                  onDelete={handleDeleteNodeFromList}
                  onClone={handleCloneNodeFromList}
                  scriptName={script?.name}
                  transitions={transitions as any}
                  idToNameMap={idToNameMap}
                  onTransitionClick={handleTransitionClick}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <NodeEditorSidebar
        open={sidebarOpen && (!!editingNode || isCreating)}
        onClose={() => {
          setSidebarOpen(false);
          setEditingNode(null);
          setIsCreating(false);
        }}
        node={editingNode || undefined}
        isCreating={isCreating}
        scripts={[]} // scripts not needed here
        nodes={nodesForSelection}
        transitions={transitions}
        onTransitionsChange={(next) => setTransitions(next as any[])}
        onSubmit={isCreating ? handleCreateNode : handleUpdateNode}
        onDelete={!isCreating ? handleDeleteNode : undefined}
        isSubmitting={isSubmitting}
        showScriptSelect={false}
        isFlowPage={true}
        scriptIdForContext={scriptIdNum}
        readOnly={
          script?.status !== "draft" || !canEdit || isSpecialEditingNode
        }
      />
    </div>
  );
}
